import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { RefreshCw, ArrowRight, CalendarDays } from 'lucide-react';
import CoachMetrics from './CoachMetrics';

const API = process.env.REACT_APP_BACKEND_URL;

const formatPct = (v) => `${v}%`;
const formatCurrency = (v) => {
  if (!v) return '£0';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(v);
};

const MONTH_LABELS = { '01':'Jan','02':'Feb','03':'Mar','04':'Apr','05':'May','06':'Jun','07':'Jul','08':'Aug','09':'Sep','10':'Oct','11':'Nov','12':'Dec' };
const formatMonth = (key) => {
  const [y, m] = key.split('-');
  return `${MONTH_LABELS[m] || m} ${y.slice(2)}`;
};

const formatWeek = (mondayStr) => {
  const mon = new Date(mondayStr + 'T00:00:00Z');
  const sun = new Date(mon);
  sun.setUTCDate(mon.getUTCDate() + 6);
  const opts = { day: 'numeric', month: 'short', timeZone: 'UTC' };
  const yearOpts = { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' };
  return `${mon.toLocaleDateString('en-GB', opts)} – ${sun.toLocaleDateString('en-GB', yearOpts)}`;
};

const PERIODS = [
  { key: 1, label: 'This Month' },
  { key: 3, label: '3 Months' },
  { key: 6, label: '6 Months' },
  { key: 12, label: '12 Months' },
];

export default function LeadTrackerTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [months, setMonths] = useState(3);
  const [customRange, setCustomRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [activeCustom, setActiveCustom] = useState(null); // {start, end} once applied

  const fetchData = useCallback(async (force = false) => {
    setLoading(true);
    try {
      let url = `${API}/api/lead-tracker?months=${months}`;
      if (activeCustom) {
        url = `${API}/api/lead-tracker?start_date=${activeCustom.start}&end_date=${activeCustom.end}`;
      }
      if (force) url += (url.includes('?') ? '&' : '?') + 'refresh=1';
      const res = await fetch(url);
      if (res.ok) setData(await res.json());
    } catch (e) {
      console.error('Failed to fetch lead tracker:', e); // eslint-disable-line no-console
    }
    setLoading(false);
  }, [months, activeCustom]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh ticker: while the snapshot is updating, quietly re-fetch until fresh.
  useEffect(() => {
    if (data?.updating) {
      const t = setTimeout(() => fetchData(), 8000);
      return () => clearTimeout(t);
    }
  }, [data?.updating, fetchData]);

  const applyCustomRange = () => {
    if (startDate && endDate) {
      setActiveCustom({ start: startDate, end: endDate });
    }
  };

  const selectPreset = (key) => {
    setMonths(key);
    setCustomRange(false);
    setActiveCustom(null);
  };

  const [breakdownView, setBreakdownView] = useState('monthly');
  const total = data?.total || {};
  const sources = data?.funnelBySource || [];
  const monthly = data?.monthly || [];
  const weekly = data?.weekly || [];

  const pctColor = (v) => v >= 50 ? 'text-green-600' : v >= 25 ? 'text-amber-600' : 'text-red-600';

  return (
    <div className="space-y-6" data-testid="lead-tracker-tab">
      <CoachMetrics />

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold text-stone-800">Lead Tracker</h2>
          <p className="text-sm text-stone-500">
            Lead → Call → Won/Lost
            {data && ` · ${data.totalDeals} deals tracked`}
            {data?.cachedAt && ` · data cached ${new Date(data.cachedAt).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })} (Refresh for live)`}
            {data?.updating && <span className="text-amber-600" data-testid="lead-tracker-updating"> · updating…</span>}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-stone-100 rounded-lg p-0.5">
            {PERIODS.map(p => (
              <button
                key={p.key}
                onClick={() => selectPreset(p.key)}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                  months === p.key && !activeCustom ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
                }`}
                data-testid={`period-${p.key}`}
              >
                {p.label}
              </button>
            ))}
            <button
              onClick={() => setCustomRange(!customRange)}
              className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 ${
                activeCustom ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'
              }`}
            >
              <CalendarDays className="w-3.5 h-3.5" /> Custom
            </button>
          </div>
          {customRange && (
            <div className="flex items-center gap-1.5">
              <Input
                type="date"
                value={startDate}
                onChange={e => setStartDate(e.target.value)}
                className="h-8 text-xs w-[130px]"
              />
              <span className="text-xs text-stone-400">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={e => setEndDate(e.target.value)}
                className="h-8 text-xs w-[130px]"
              />
              <Button
                onClick={applyCustomRange}
                disabled={!startDate || !endDate || loading}
                size="sm"
                className="bg-stone-900 text-white hover:bg-stone-800 h-8 text-xs"
                data-testid="apply-custom-range"
              >
                Apply
              </Button>
            </div>
          )}
          <Button onClick={() => fetchData(true)} disabled={loading} variant="outline" size="sm" className="border-stone-200" data-testid="refresh-tracker" title="Refresh live from Brevo now">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      {/* Overall Funnel Cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card className="bg-white border-stone-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Leads</p>
            <p className="text-3xl font-bold text-stone-900 mt-1">{total.leads || 0}</p>
            <p className="text-xs text-stone-400 mt-1">New leads in period</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Had Call</p>
            <p className="text-3xl font-bold text-stone-900 mt-1">{total.hadCall || 0}</p>
            <p className={`text-sm font-semibold mt-1 ${pctColor(total.leadToCall)}`}>{formatPct(total.leadToCall || 0)} of new leads</p>
            {total.callsBooked > 0 && (
              <p className="text-xs text-blue-600 mt-1" data-testid="calls-booked-stat">{total.callsBooked} calls held in period</p>
            )}
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Won</p>
            <p className="text-3xl font-bold text-green-600 mt-1">{total.won || 0}</p>
            <p className="text-xs text-stone-400">{formatCurrency(total.wonRevenue)} · booked in period</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Pending</p>
            <p className="text-3xl font-bold text-amber-500 mt-1">{total.pending || 0}</p>
            <p className="text-xs text-stone-400 mt-1">Had call, awaiting outcome</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">Lost</p>
            <p className="text-3xl font-bold text-red-500 mt-1">{total.lost || 0}</p>
            <p className="text-xs text-stone-400 mt-1">Lost in period (by close date)</p>
          </CardContent>
        </Card>
        <Card className="bg-white border-stone-100 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs text-stone-500 uppercase tracking-wider">No Call Yet</p>
            <p className="text-3xl font-bold text-stone-400 mt-1">{total.noCallYet || 0}</p>
            <p className="text-xs text-stone-400 mt-1">Still in pipeline</p>
          </CardContent>
        </Card>
      </div>

      {/* Conversion Flow */}
      <Card className="bg-white border-stone-100 shadow-sm">
        <CardContent className="p-5">
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <div className="text-center">
              <p className="text-2xl font-bold text-stone-900">{total.leads || 0}</p>
              <p className="text-xs text-stone-500">Leads</p>
            </div>
            <div className="flex flex-col items-center">
              <ArrowRight className="w-5 h-5 text-stone-400" />
              <p className={`text-xs font-bold ${pctColor(total.leadToCall)}`}>{formatPct(total.leadToCall || 0)}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-stone-900">{total.hadCall || 0}</p>
              <p className="text-xs text-stone-500">Had Call</p>
            </div>
            <div className="flex flex-col items-center">
              <ArrowRight className="w-5 h-5 text-stone-400" />
              <p className={`text-xs font-bold ${pctColor(total.callToWon)}`}>{formatPct(total.callToWon || 0)}</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-green-600">{total.won || 0}</p>
              <p className="text-xs text-stone-500">Won</p>
            </div>
            <div className="mx-2 border-l border-stone-200 h-10" />
            <div className="text-center">
              <p className="text-2xl font-bold text-amber-500">{total.pending || 0}</p>
              <p className="text-xs text-stone-500">Pending</p>
            </div>
            <div className="text-center">
              <p className="text-2xl font-bold text-red-500">{total.lost || 0}</p>
              <p className="text-xs text-stone-500">Lost</p>
            </div>
            <div className="mx-2 border-l border-stone-200 h-10" />
            <div className="text-center">
              <p className="text-2xl font-bold text-stone-400">{total.noCallYet || 0}</p>
              <p className="text-xs text-stone-500">No Call Yet</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* By Source Table */}
      <Card className="bg-white border-stone-100 shadow-sm overflow-hidden">
        <CardHeader className="pb-3 border-b border-stone-100">
          <CardTitle className="text-base font-medium text-stone-800">Funnel by Lead Source</CardTitle>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="source-table">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-stone-600">Source</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Leads</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Had Call</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Lead→Call</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Won</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Pending</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Lost</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">No Call</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Call→Won</th>
                <th className="text-right py-3 px-4 font-medium text-stone-600">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {sources.map(s => (
                <tr key={s.source} className="border-t border-stone-100 hover:bg-stone-50">
                  <td className="py-2.5 px-4 font-medium text-stone-800">{s.source}</td>
                  <td className="py-2.5 px-3 text-right text-stone-700">{s.leads}</td>
                  <td className="py-2.5 px-3 text-right text-stone-700">{s.hadCall}</td>
                  <td className={`py-2.5 px-3 text-right font-semibold ${pctColor(s.leadToCall)}`}>{formatPct(s.leadToCall)}</td>
                  <td className="py-2.5 px-3 text-right text-green-600 font-medium">{s.won}</td>
                  <td className="py-2.5 px-3 text-right text-amber-500 font-medium">{s.pending}</td>
                  <td className="py-2.5 px-3 text-right text-red-500">{s.lost}</td>
                  <td className="py-2.5 px-3 text-right text-stone-400">{s.noCallYet}</td>
                  <td className={`py-2.5 px-3 text-right font-semibold ${pctColor(s.callToWon)}`}>{formatPct(s.callToWon)}</td>
                  <td className="py-2.5 px-4 text-right font-medium text-stone-800">{formatCurrency(s.wonRevenue)}</td>
                </tr>
              ))}
              <tr className="border-t-2 border-stone-300 bg-stone-50 font-bold">
                <td className="py-3 px-4 text-stone-800">TOTAL</td>
                <td className="py-3 px-3 text-right text-stone-800">{total.leads}</td>
                <td className="py-3 px-3 text-right text-stone-800">{total.hadCall}</td>
                <td className={`py-3 px-3 text-right ${pctColor(total.leadToCall)}`}>{formatPct(total.leadToCall || 0)}</td>
                <td className="py-3 px-3 text-right text-green-600">{total.won}</td>
                <td className="py-3 px-3 text-right text-amber-500">{total.pending}</td>
                <td className="py-3 px-3 text-right text-red-500">{total.lost}</td>
                <td className="py-3 px-3 text-right text-stone-400">{total.noCallYet}</td>
                <td className={`py-3 px-3 text-right ${pctColor(total.callToWon)}`}>{formatPct(total.callToWon || 0)}</td>
                <td className="py-3 px-4 text-right text-stone-800">{formatCurrency(total.wonRevenue)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>

      {/* Breakdown */}
      <Card className="bg-white border-stone-100 shadow-sm overflow-hidden">
        <CardHeader className="pb-3 border-b border-stone-100">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-medium text-stone-800">Breakdown</CardTitle>
            <div className="flex bg-stone-100 rounded-lg p-0.5">
              <button
                onClick={() => setBreakdownView('weekly')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${breakdownView === 'weekly' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
              >
                Weekly
              </button>
              <button
                onClick={() => setBreakdownView('monthly')}
                className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${breakdownView === 'monthly' ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-500 hover:text-stone-700'}`}
              >
                Monthly
              </button>
            </div>
          </div>
        </CardHeader>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50">
              <tr>
                <th className="text-left py-3 px-4 font-medium text-stone-600">{breakdownView === 'weekly' ? 'Week' : 'Month'}</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Leads</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Had Call</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Lead→Call</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Won</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Pending</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Lost</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">No Call</th>
                <th className="text-right py-3 px-3 font-medium text-stone-600">Call→Won</th>
                <th className="text-right py-3 px-4 font-medium text-stone-600">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {breakdownView === 'monthly' ? (
                monthly.map(m => (
                  <tr key={m.month} className="border-t border-stone-100 hover:bg-stone-50">
                    <td className="py-2.5 px-4 font-medium text-stone-800">{formatMonth(m.month)}</td>
                    <td className="py-2.5 px-3 text-right text-stone-700">{m.leads}</td>
                    <td className="py-2.5 px-3 text-right text-stone-700">{m.hadCall}</td>
                    <td className={`py-2.5 px-3 text-right font-semibold ${pctColor(m.leadToCall)}`}>{formatPct(m.leadToCall)}</td>
                    <td className="py-2.5 px-3 text-right text-green-600 font-medium">{m.won}</td>
                    <td className="py-2.5 px-3 text-right text-amber-500">{m.pending}</td>
                    <td className="py-2.5 px-3 text-right text-red-500">{m.lost}</td>
                    <td className="py-2.5 px-3 text-right text-stone-400">{m.noCallYet}</td>
                    <td className={`py-2.5 px-3 text-right font-semibold ${pctColor(m.callToWon)}`}>{formatPct(m.callToWon)}</td>
                    <td className="py-2.5 px-4 text-right font-medium text-stone-800">{formatCurrency(m.wonRevenue)}</td>
                  </tr>
                ))
              ) : (
                weekly.map(w => (
                  <tr key={w.week} className="border-t border-stone-100 hover:bg-stone-50">
                    <td className="py-2.5 px-4 font-medium text-stone-800 text-xs">{formatWeek(w.week)}</td>
                    <td className="py-2.5 px-3 text-right text-stone-700">{w.leads}</td>
                    <td className="py-2.5 px-3 text-right text-stone-700">{w.hadCall}</td>
                    <td className={`py-2.5 px-3 text-right font-semibold ${pctColor(w.leadToCall)}`}>{formatPct(w.leadToCall)}</td>
                    <td className="py-2.5 px-3 text-right text-green-600 font-medium">{w.won}</td>
                    <td className="py-2.5 px-3 text-right text-amber-500">{w.pending}</td>
                    <td className="py-2.5 px-3 text-right text-red-500">{w.lost}</td>
                    <td className="py-2.5 px-3 text-right text-stone-400">{w.noCallYet}</td>
                    <td className={`py-2.5 px-3 text-right font-semibold ${pctColor(w.callToWon)}`}>{formatPct(w.callToWon)}</td>
                    <td className="py-2.5 px-4 text-right font-medium text-stone-800">{formatCurrency(w.wonRevenue)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
